/**
 * Store de toasts fora do React (padrão external store), para que `toast.success(...)` possa ser
 * chamado de qualquer lugar — inclusive de callbacks de mutação — sem prop-drilling nem hook.
 * O `<Toaster/>` assina via `useSyncExternalStore`. Mantém a lista imutável (reatribuída a cada
 * mudança) para o snapshot ser referencialmente estável entre renders.
 */
export type ToastVariant = 'success' | 'error' | 'info' | 'warning'

export interface Toast {
  readonly id: string
  readonly variant: ToastVariant
  readonly title: string
  readonly description?: string
  /** ms até auto-dispensar; `0` = fixo (só sai no clique). */
  readonly duration: number
}

export interface ToastOptions {
  readonly description?: string
  readonly duration?: number
}

/** Máximo de toasts empilhados ao mesmo tempo (os mais antigos saem). */
const MAX_TOASTS = 5
const DEFAULT_DURATION = 4000
const ERROR_DURATION = 6000

let toasts: readonly Toast[] = []
let counter = 0
const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) {
    listener()
  }
}

function push(
  variant: ToastVariant,
  title: string,
  options: ToastOptions | undefined,
  fallbackDuration: number,
): string {
  counter += 1
  const id = `toast-${counter.toString()}`
  const toast: Toast = {
    id,
    variant,
    title,
    ...(options?.description !== undefined ? { description: options.description } : {}),
    duration: options?.duration ?? fallbackDuration,
  }
  // Prepend (mais novo no topo) e limita a pilha descartando os mais antigos.
  toasts = [toast, ...toasts].slice(0, MAX_TOASTS)
  emit()
  return id
}

export function subscribeToasts(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export function getToasts(): readonly Toast[] {
  return toasts
}

export function dismissToast(id: string): void {
  const next = toasts.filter((toast) => toast.id !== id)
  if (next.length !== toasts.length) {
    toasts = next
    emit()
  }
}

/** Zera a pilha — usado em testes e ao trocar de contexto crítico. */
export function clearToasts(): void {
  if (toasts.length > 0) {
    toasts = []
    emit()
  }
}

/**
 * API imperativa de toasts. Passe SEMPRE strings já traduzidas (i18n é do chamador); o store não
 * conhece chaves de tradução.
 */
export const toast = {
  success: (title: string, options?: ToastOptions): string =>
    push('success', title, options, DEFAULT_DURATION),
  error: (title: string, options?: ToastOptions): string =>
    push('error', title, options, ERROR_DURATION),
  info: (title: string, options?: ToastOptions): string =>
    push('info', title, options, DEFAULT_DURATION),
  warning: (title: string, options?: ToastOptions): string =>
    push('warning', title, options, DEFAULT_DURATION),
  dismiss: dismissToast,
}
